import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, User, LogOut, Moon, Sun } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  badge?: string;
}

export interface SidebarUser {
  name: string;
  email: string;
  avatar?: string;
}

export interface SidebarProps {
  id?: string;
  /**
   * Brand logo component or image URL
   */
  logo?: React.ReactNode;
  /**
   * Application title
   */
  title: string;
  /**
   * Navigation items
   */
  navItems: NavItem[];
  /**
   * User information
   */
  user: SidebarUser;
  /**
   * Current active path for highlighting
   */
  activePath?: string;
  /**
   * Dark mode state
   */
  isDarkMode: boolean;
  /**
   * Dark mode toggle handler
   */
  onToggleDarkMode: () => void;
  /**
   * Logout handler
   */
  onLogout: () => void;
  /**
   * Account settings handler (optional)
   */
  onAccountSettings?: () => void;
  /**
   * Link component for navigation (e.g., React Router Link, Next.js Link)
   */
  LinkComponent?: React.ComponentType<any> | 'a';
  /**
   * Additional className for sidebar
   */
  className?: string;
}

const COLLAPSED_KEY = 'sidebar-collapsed';

export function Sidebar({
  id,
  logo,
  title,
  navItems,
  user,
  activePath,
  isDarkMode,
  onToggleDarkMode,
  onLogout,
  onAccountSettings,
  LinkComponent = 'a',
  className = '',
}: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(COLLAPSED_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const activeHref = navItems
    .filter((item) => activePath === item.href || activePath?.startsWith(`${item.href}/`))
    .sort((a, b) => b.href.length - a.href.length)[0]?.href;

  return (
    <aside
      id={id}
      aria-label={`${title} navigation`}
      className={`${
        isCollapsed ? 'w-16' : 'w-64'
      } bg-[hsl(var(--color-card))] border-r border-border h-full flex flex-col transition-all duration-300 ${className}`}
    >
      {/* Top Section: Logo, Title, Collapse Button */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        {isCollapsed ? (
          <Button
            onClick={toggleCollapse}
            variant="ghost"
            size="icon"
            className="w-8 h-8"
            title="Expand sidebar"
            aria-label="Expand sidebar"
            aria-expanded={false}
          >
            <ChevronRight className="w-5 h-5" aria-hidden="true" />
          </Button>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {logo && <div className="flex-shrink-0">{logo}</div>}
              <span className="font-bold text-lg text-[hsl(var(--color-foreground))] truncate">
                {title}
              </span>
            </div>
            <Button
              onClick={toggleCollapse}
              variant="ghost"
              size="icon"
              className="w-8 h-8 flex-shrink-0"
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
              aria-expanded={true}
            >
              <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            </Button>
          </>
        )}
      </div>

      {/* Middle Section: Navigation Items */}
      <nav className="flex-1 overflow-y-auto p-3" aria-label={`${title} navigation`}>
        <div className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeHref === item.href;
            const linkClassName = `flex w-full items-center gap-3 px-3 py-2 rounded-lg hover:bg-[hsl(var(--color-secondary))] transition text-[hsl(var(--color-foreground))] ${
              isActive ? 'bg-[hsl(var(--color-secondary))] font-medium' : ''
            } ${isCollapsed ? 'justify-center' : ''}`;
            const collapsedTitle = item.badge ? `${item.label}, ${item.badge}` : item.label;
            const itemLabel = !isCollapsed && (
              <>
                <span className="truncate">{item.label}</span>
                {item.badge && (
                  <span className="ml-auto shrink-0 rounded-full border border-border px-1.5 text-[10px] font-medium leading-4 text-muted-foreground">
                    {item.badge}
                  </span>
                )}
              </>
            );

            if (LinkComponent === 'a') {
              return (
                <a
                  key={item.href}
                  href={item.href}
                  className={linkClassName}
                  title={isCollapsed ? collapsedTitle : undefined}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                  {itemLabel}
                </a>
              );
            }

            const CustomLink = LinkComponent;
            return (
              <CustomLink
                key={item.href}
                to={item.href}
                className={linkClassName}
                title={isCollapsed ? collapsedTitle : undefined}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                {itemLabel}
              </CustomLink>
            );
          })}
        </div>
      </nav>

      {/* Bottom Section: Profile Dropdown */}
      <div className="p-3 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[hsl(var(--color-secondary))] transition ${
                isCollapsed ? 'justify-center' : ''
              }`}
              aria-label={`Account menu for ${user.name}`}
            >
              <Avatar className="w-8 h-8 flex-shrink-0">
                <AvatarFallback className="bg-[hsl(var(--color-primary-action))] text-[hsl(var(--color-primary-action-foreground))] text-sm font-medium">
                  {getUserInitials(user.name)}
                </AvatarFallback>
              </Avatar>
              {!isCollapsed && (
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-[hsl(var(--color-foreground))] truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {onAccountSettings && (
              <DropdownMenuItem onClick={onAccountSettings}>
                <User className="mr-2 h-4 w-4" />
                <span>Account</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onToggleDarkMode}>
              {isDarkMode ? (
                <>
                  <Sun className="mr-2 h-4 w-4" />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="mr-2 h-4 w-4" />
                  <span>Dark Mode</span>
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}

